/**
 * What was just earned, said where it can be seen without looking for it.
 *
 * A number floats up beside the character for every bit of experience, and a
 * level reached says so for a moment longer. The chat log already carries both,
 * but a log is something somebody reads afterwards: the point of these is that
 * a swing, a catch or a bar is answered on the spot.
 *
 * Nothing here talks to the world. What arrives is what the world already sent,
 * so a drop is only ever a repeat of something that has already happened.
 */

import { useEffect, useState } from 'react';
import { SKILL_NAMES } from '@inverse/common';

/** One thing earned, and when it was earned, so it can be taken away again. */
export interface Earned {
  id: number;
  skill: number;
  gained: number;
  level: number;
  levelled: boolean;
  at: number;
}

/** How long a number floats for, and how long a level is announced for. */
const DROP_MS = 2200;
const LEVEL_MS = 4200;

/** A skill as it is written where somebody reads it. */
function named(skill: number): string {
  return SKILL_NAMES[skill] ?? 'Skill';
}

export function Trained({
  earned,
  onDone,
  onGuide,
}: {
  earned: readonly Earned[];
  /** Takes away whatever has had its time, so the list does not grow forever. */
  onDone: (before: number) => void;
  /** Opening the guide for a skill, which is what a level reached invites. */
  onGuide: (skill: string) => void;
}): JSX.Element | null {
  const [now, setNow] = useState(() => Date.now());

  // A beat rather than a frame: these move by animation, and this is only here
  // to notice when one has finished.
  useEffect(() => {
    if (earned.length === 0) return;

    const timer = window.setInterval(() => {
      const held = Date.now();
      setNow(held);
      onDone(held - LEVEL_MS);
    }, 400);

    return () => window.clearInterval(timer);
  }, [earned.length, onDone]);

  const drops = earned.filter((one) => one.gained > 0 && now - one.at < DROP_MS);
  const reached = earned.filter((one) => one.levelled && now - one.at < LEVEL_MS);

  if (drops.length === 0 && reached.length === 0) return null;

  return (
    <>
      <div className="xp-drops" aria-hidden>
        {drops.map((one) => (
          <span className="xp-drop" key={one.id}>
            {named(one.skill)} <b>+{one.gained}</b>
          </span>
        ))}
      </div>

      {/* A level is worth a moment of its own, and worth being able to press:
          whoever just reached one is exactly who wants to know what it opened. */}
      {reached.length > 0 ? (
        <div className="levelled">
          {reached.map((one) => (
            <button
              type="button"
              className="levelled-note"
              key={`level-${one.id}`}
              title={`What ${named(one.skill)} is for`}
              onClick={() => onGuide(named(one.skill).toLowerCase())}
            >
              <b>
                {named(one.skill)} level {one.level}
              </b>
              <small>See what it opens</small>
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
